// STUDIO-BLUETOOTH-PRODUCT-CONTROL-1 · 리모컨 모드 ON 동안 '펜' 입력만 가로채 Studio 버튼에 닿지 않게 막고,
// 한 번의 누름~뗌 동작을 화면 기준 좌표로 전달. 손가락(touch)·마우스 입력은 그대로 통과.
// 한계: 실제 S펜과 리모컨 가상 펜을 브라우저에서 구별할 수 없어 모드 ON 동안 모든 펜 입력이 차단된다.
import { useEffect, useRef } from 'react';

const PEN_EVENTS = ['pointerdown', 'pointermove', 'pointerup', 'pointercancel', 'lostpointercapture', 'contextmenu', 'auxclick', 'dblclick'];
const CLICK_WINDOW_MS = 700;
// TOUCH-GUARD-SAFETY-FIX-1 · 펜 종료 신호 누락 대비: 마지막 펜 이벤트 후 400ms 가 지나면 손가락 차단 해제,
// 1500ms 동안 아무 이벤트가 없으면 미완료 누름을 명령 없이 폐기(중복·지연 실행 없음).
export const PEN_TOUCH_BLOCK_MS = 400;
export const PEN_STALE_MS = 1500;
const PEN_ECHO_MS = 120;

// SHUTTER-QUICKADD-TOUCH-FIX-1 · touchBlock=false: 펜 가로채기는 유지하고 손가락 touch 차단만 해제(빠른등록 등 입력 화면).
export default function useRemoteGuard(enabled, onGesture, { touchBlock = true } = {}) {
  const cb = useRef(onGesture);
  cb.current = onGesture;

  useEffect(() => {
    if (!enabled) { return undefined; }
    const g = {};
    const stale = {};
    let lastPenAt = -1e9;
    const penDown = () => Object.keys(g).length;
    const drop = (id) => { clearTimeout(stale[id]); delete stale[id]; delete g[id]; };
    const arm = (id) => { clearTimeout(stale[id]); stale[id] = setTimeout(() => drop(id), PEN_STALE_MS); };
    const block = (e) => { e.stopImmediatePropagation(); if (e.cancelable) e.preventDefault(); };
    const onPen = (e) => {
      if (e.pointerType !== 'pen') return;
      block(e);
      const now = performance.now();
      lastPenAt = now;
      const id = e.pointerId;
      if (e.type === 'pointerdown') {
        drop(id);
        g[id] = { sx: e.screenX, sy: e.screenY, ex: e.screenX, ey: e.screenY, t: now, cx: e.clientX, cy: e.clientY };
        arm(id);
      } else if (e.type === 'pointermove' && g[id]) {
        g[id].ex = e.screenX; g[id].ey = e.screenY;
        arm(id);
      } else if ((e.type === 'pointerup' || e.type === 'pointercancel' || e.type === 'lostpointercapture') && g[id]) {
        const s = g[id];
        drop(id);
        // pointercancel·lostpointercapture 좌표는 의미 없음 → 마지막 이동 좌표 사용. 종료는 한 번만 전달.
        const end = e.type === 'pointerup' ? { ex: e.screenX, ey: e.screenY } : { ex: s.ex, ey: s.ey };
        cb.current({ ...s, ...end, sw: window.screen.width, sh: window.screen.height, cancelled: e.type !== 'pointerup', heldMs: now - s.t });
      }
    };
    // 펜 동작 직후의 click(pointerType 'pen' 또는 미표기)은 Studio 버튼으로 전달하지 않음.
    const onClick = (e) => {
      const recentPen = performance.now() - lastPenAt < CLICK_WINDOW_MS;
      if (e.pointerType === 'pen' || (recentPen && !e.pointerType)) block(e);
    };
    // 스타일러스 터치 또는 펜 누름 중에 따라오는 터치의 스크롤을 막음(펜 없는 손가락 터치는 영향 없음).
    // SCROLL-FIX-1 · 가상 펜의 touchType 이 'stylus' 가 아닐 수 있어 펜 누름 상태도 함께 확인.
    const onTouch = (e) => {
      const t = e.changedTouches && e.changedTouches[0];
      const since = performance.now() - lastPenAt;
      const finger = t && t.touchType === 'direct';
      const penLike = (t && t.touchType === 'stylus') || since < PEN_ECHO_MS
        || (!finger && penDown() > 0 && since < PEN_TOUCH_BLOCK_MS);
      if (penLike) { e.stopImmediatePropagation(); if (e.cancelable) e.preventDefault(); }
    };
    const onWheel = (e) => { if (performance.now() - lastPenAt < 300) { e.stopImmediatePropagation(); if (e.cancelable) e.preventDefault(); } };
    PEN_EVENTS.forEach((t) => window.addEventListener(t, onPen, true));
    window.addEventListener('click', onClick, true);
    if (touchBlock) ['touchstart', 'touchmove'].forEach((t) => window.addEventListener(t, onTouch, { capture: true, passive: false }));
    window.addEventListener('wheel', onWheel, { capture: true, passive: false });
    return () => {
      PEN_EVENTS.forEach((t) => window.removeEventListener(t, onPen, true));
      window.removeEventListener('click', onClick, true);
      ['touchstart', 'touchmove'].forEach((t) => window.removeEventListener(t, onTouch, { capture: true }));
      window.removeEventListener('wheel', onWheel, { capture: true });
      Object.keys(g).forEach(drop);
    };
  }, [enabled, touchBlock]);
}
